import React, { ComponentType } from 'react';
import styled from 'styled-components';
import noop from 'utils/noop';
import { BasicSelect } from 'ui/selects/BasicSelect';
import { BUTTON_SIZES, BUTTON_VARIANTS, IconButton } from 'ui/button';
import { subtitle2, subtleShadow, spacing } from 'ui/theme';
import { DoubleArrow } from 'ui/icons/navigation/DoubleArrow';
import { DownArrow } from 'ui/icons/navigation/DownArrow';
import { UpArrow } from 'ui/icons/navigation/UpArrow';
import { SelectValueDisplay } from 'ui/selects/SelectValueDisplay';
import { SelectValueDisplayProps } from 'ui/selects/types';
import { ALIGNMENT } from 'ui/selects/constants';
import { SORT_DIRECTIONS } from 'constants/sortDirectionsEnum';
import { DEFAULT_OPTIONS, LABEL_TEXT } from './constants';

const SortSelectWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.half};
`;

const SortSelectText = styled.p`
  ${subtitle2}
`;

const DoubleArrowButton = styled(IconButton)`
  ${subtleShadow}
`;

const SortSelectTrigger = styled(SelectValueDisplay)`
  width: ${spacing.x12};
`;

export type SortSelectProps = {
  /** handle sort function takes(value, direction) */
  handleSort: (value: string, direction: SORT_DIRECTIONS) => void;
  /** sort direction defaults to null */
  sortDirection: SORT_DIRECTIONS | null;
  /** sort value defaults to first option in options */
  sortedBy: string;
  /** alignment of select */
  alignment?: (typeof ALIGNMENT)[keyof typeof ALIGNMENT];
  /** options in select, defaults to constant */
  options: Array<any>;
};

export const SortSelect = ({
  handleSort = noop,
  sortDirection,
  sortedBy = DEFAULT_OPTIONS[0].value,
  options = DEFAULT_OPTIONS,
  alignment = ALIGNMENT.RIGHT,
  ...props
}: SortSelectProps) => {
  const isAscendingSort = sortDirection === SORT_DIRECTIONS.ASCENDING;
  const isDescendingSort = sortDirection === SORT_DIRECTIONS.DESCENDING;

  const handleSelect = (selected: string[]) => {
    if (sortDirection) handleSort(selected[0], sortDirection);
    // if sort direction is not chosen but the select is selected default to descending
    else handleSort(selected[0], SORT_DIRECTIONS.DESCENDING);
  };

  const handleDoubleArrowClick = () => {
    const newSortDirection =
      !sortDirection || isAscendingSort ? SORT_DIRECTIONS.DESCENDING : SORT_DIRECTIONS.ASCENDING;
    handleSort(sortedBy, newSortDirection);
  };
  return (
    <SortSelectWrapper data-test="sort-select-wrapper" {...props}>
      <SortSelectText data-test="sort-select-label">{LABEL_TEXT}</SortSelectText>
      <BasicSelect
        defaultValues={[sortedBy]}
        options={options}
        onSelect={handleSelect}
        data-test="sort-select"
        Trigger={
          SortSelectTrigger as ComponentType<React.PropsWithChildren<SelectValueDisplayProps>>
        }
        horizontalAlignment={alignment}
      />
      <DoubleArrowButton
        variant={BUTTON_VARIANTS.SECONDARY}
        size={BUTTON_SIZES.LARGE}
        onClick={handleDoubleArrowClick}
        aria-label="direction-toggle"
        data-test="sort-select-direction-toggle"
      >
        {!sortDirection && <DoubleArrow data-test="sort-select-direction-toggle-doublearrow" />}
        {isAscendingSort && <UpArrow data-test="sort-select-direction-toggle-uparrow" />}
        {isDescendingSort && <DownArrow data-test="sort-select-direction-toggle-downarrow" />}
      </DoubleArrowButton>
    </SortSelectWrapper>
  );
};
